import type { RechargeFeature, RechargeFeatureMode, RechargeSettingsSummary } from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import type { JSX } from 'react';
import type { DataTableEmptyState } from '@/components/data-table/data-table.js';
import { Button } from '@/components/ui/button.js';
import { cn } from '@/lib/cn.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchRechargeSettings } from '../settings/api-recharge.js';

/**
 * États communs aux onglets de l'écran Abonnements (`06` §8) : bannière de
 * mode et « Recharge non configuré ». Lit `GET /api/recharge/settings` avec
 * la même clé et le même fetcher que Paramètres → Recharge : le cache est
 * partagé, un réglage enregistré se reflète ici sans rechargement.
 */
export function useRechargeSummary() {
  return useQuery({
    queryKey: queryKeys.rechargeSettings.summary(),
    queryFn: fetchRechargeSettings,
  });
}

/** Le mode test global force la simulation de toute fonction non désactivée (`06` §0). */
export function effectiveFeatureMode(
  summary: RechargeSettingsSummary,
  feature: RechargeFeature,
): RechargeFeatureMode {
  const mode = summary.features[feature].mode;
  return summary.testMode && mode !== 'disabled' ? 'simulation' : mode;
}

/**
 * Vrai seulement quand le serveur a répondu que le token manque : pendant le
 * chargement ou en erreur, on ne présume rien (pas de faux « non configuré »).
 */
export function isRechargeTokenMissing(summary: RechargeSettingsSummary | undefined): boolean {
  return summary?.apiTokenConfigured === false;
}

function RechargeSettingsLink({ children }: { children: string }): JSX.Element {
  return (
    <Link
      to="/parametres"
      search={{ tab: 'recharge' }}
      className="rounded-sm font-medium underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </Link>
  );
}

const BANNER_COPY: Readonly<Record<Exclude<RechargeFeatureMode, 'active'>, { title: string; body: string }>> = {
  simulation: {
    title: 'Simulation',
    body: 'Flux calcule tout mais n’écrit rien chez Recharge et ne publie rien sur Slack',
  },
  disabled: { title: 'Fonction désactivée', body: 'Les webhooks reçus sont ignorés' },
};

/**
 * Bannière de section en tête d'un onglet dont la fonction n'est pas active
 * (`06` §8, communs). Rien tant que les réglages ne sont pas chargés : jamais
 * de mode supposé.
 */
export function RechargeModeBanner({ feature }: { feature: RechargeFeature }): JSX.Element | null {
  const query = useRechargeSummary();
  if (!query.data) {
    return null;
  }
  const mode = effectiveFeatureMode(query.data, feature);
  return mode === 'active' ? null : <ModeBanner mode={mode} copy={BANNER_COPY[mode]} />;
}

/** Bannière de mode, pour une fonction hors `RECHARGE_FEATURES` (synchronisation Klaviyo). */
export function ModeBanner({
  mode,
  copy,
}: {
  mode: Exclude<RechargeFeatureMode, 'active'>;
  copy: { title: string; body: string };
}): JSX.Element {
  return (
    <div
      data-testid="recharge-mode-banner"
      className={cn(
        'flex flex-wrap items-start gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm',
        mode === 'simulation'
          ? 'border-status-orange-border bg-status-orange-bg text-status-orange-fg'
          : 'border-status-gray-border bg-status-gray-bg text-status-gray-fg',
      )}
    >
      <p className="flex min-w-0 flex-1 items-start gap-2">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          <span className="font-medium">{copy.title}</span> · {copy.body}
        </span>
      </p>
      <RechargeSettingsLink>Modifier dans Paramètres</RechargeSettingsLink>
    </div>
  );
}

/** État vide « fonction non configurée » commun aux onglets (`06` §8, communs). */
export const RECHARGE_NOT_CONFIGURED_EMPTY_STATE: DataTableEmptyState = {
  title: 'Connectez Recharge pour commencer',
  description: 'Ajoutez le token API dans Paramètres.',
  action: (
    <Button asChild variant="outline">
      <Link to="/parametres" search={{ tab: 'recharge' }}>
        Ouvrir les réglages Recharge
      </Link>
    </Button>
  ),
};
